import React, { useState } from 'react';
import { StyleSheet, View, TouchableOpacity, Platform, Linking } from 'react-native';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ODSText } from '../../atoms/ODSText';
import { ODSIcon } from '../../atoms/ODSIcon';
import ODSTabs from '../../atoms/ODSTabs';
import RentReviewDetailCard, { RentReviewSkeleton, DocumentItem } from '../RentReviewDetailCard';
import { getTokens } from '../../../design-tokens/color-tokens';

const TAB_LABELS = ['Active\nRent Reviews', 'Past\nRent Reviews', 'Archived\nRent Reviews'];

const formatDate = (d: any) => (d ? moment(d).format('DD/MM/YYYY') : '—');

const getStatusColors = (status = '') => {
  const s = String(status).toLowerCase();
  if (s.includes('accept') || s.includes('sign') || s.includes('complet'))
    return { bg: '#D1FAE5', text: '#059669' };
  if (s.includes('reject') || s.includes('frozen'))
    return { bg: '#FEE2E2', text: '#DC2626' };
  if (s.includes('archive'))
    return { bg: '#EBF5FE', text: '#3182CE' };
  return { bg: '#FEF08A', text: '#92400E' };
};

export const RENT_REVIEW_STATUS = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  AWAITING_RESPONSE: 2,
  ACCEPTED: 3,
  REJECTED: 4,
  FROZEN: 5, // Deprecated - treated as rejected
  COMPLETED: 6,
  ARCHIVED: 7,
} as const;

export interface PastReviewItem {
  open_flow_rent_review_id?: string | number;
  id?: string | number;
  date?: string;
  createdAt?: string;
  status?: string;
  rent_review_status_label?: string;
  previousRent?: string;
  previous_rent?: string | number;
  current_rent?: string | number;
  proposedRent?: string;
  proposed_rent?: string | number;
  section4a_doc_url?: string;
  agreement_doc_url?: string;
  signed_agreement_url?: string;
  is_rent_review_signed?: boolean;
  [key: string]: any;
}

export interface PastReviewCardProps {
  item: PastReviewItem;
  onDownloadDoc?: (doc: any) => void;
}

export const PastReviewCard: React.FC<PastReviewCardProps> = ({ item, onDownloadDoc }) => {
  const statusColors = getStatusColors(item.status);
  const docs: { label: string; title: string; url?: string; icon: string; id?: string; rentReviewId?: string | number; status?: string }[] = [];
  if (item.is_rent_review_signed || item.signed_agreement_url) {
    docs.push({
      id: 'signed_agreement',
      label: 'Signed Rent Review Agreement',
      title: 'Signed Rent Review Agreement',
      status: 'Download',
      icon: 'TB-Download',
      rentReviewId: item.open_flow_rent_review_id || item.id,
      url: item.signed_agreement_url,
    });
  }
  if (item.section4a_doc_url)
    docs.push({ id: 'section4a', label: 'Section 4A Prescribed Doc', title: 'Section 4A Prescribed Doc', url: item.section4a_doc_url, icon: 'eye', status: 'View' });
  if (item.agreement_doc_url && !item.is_rent_review_signed && !item.signed_agreement_url)
    docs.push({ id: 'agreement', label: 'Rent Review Agreement (Draft)', title: 'Rent Review Agreement (Draft)', url: item.agreement_doc_url, icon: 'eye', status: 'View' });

  const openUrl = (doc: any) => {
    if (onDownloadDoc) {
      onDownloadDoc(doc);
    } else if (doc.url) {
      if (Platform.OS === 'web') {
        window.open(doc.url, '_blank', 'noreferrer');
      } else {
        Linking.openURL(doc.url);
      }
    }
  };

  return (
    <View style={pastStyles.card}>
      {/* Header */}
      <View style={pastStyles.cardHeader}>
        <ODSText type="md-bold" color="#2B6CB0">
          {item.date ? `Date: ${item.date}` : 'Past Review'}
        </ODSText>
        <View style={[pastStyles.badge, { backgroundColor: statusColors.bg }]}>
          <ODSText type="xs-bold" color={statusColors.text}>
            {String(item.status || 'ARCHIVED').toUpperCase()}
          </ODSText>
        </View>
      </View>

      {/* Rent Info */}
      {item.previousRent && item.previousRent !== '—' && (
        <View style={pastStyles.row}>
          <ODSText type="sm" color="#667085">
            <ODSText type="sm-bold" color="#344054">Previous Rent: </ODSText>
            {item.previousRent}
          </ODSText>
        </View>
      )}
      <View style={[pastStyles.row, { marginBottom: 16 }]}>
        <ODSText type="sm" color="#667085">
          <ODSText type="sm-bold" color="#344054">Proposed Rent: </ODSText>
          {item.proposedRent || '—'}
        </ODSText>
      </View>

      {/* Documents */}
      {docs.length > 0 && (
        <View style={pastStyles.docsRow}>
          {docs.map((doc) => (
            <TouchableOpacity
              key={doc.label}
              style={pastStyles.docBtn}
              activeOpacity={0.75}
              onPress={() => openUrl(doc)}
            >
              <ODSIcon icon={doc.icon} size={16} color="#1FA6B2" />
              <ODSText type="xs-bold" color="#1FA6B2" style={{ marginLeft: 6 }}>
                {doc.label}
              </ODSText>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

export interface RentReviewTabsProps {
  loading?: boolean;
  rentReviewData?: any;
  activeReview?: any;
  userId?: number | string;
  subTenantId?: number | string;
  statusLabel?: string;
  documents?: DocumentItem[];
  isResponded?: boolean;
  pastReviews?: PastReviewItem[];
  archivedReviews?: PastReviewItem[];
  items?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  onRejectOffer?: () => void;
  onSignAgreement?: () => void;
  onContactAgent?: () => void;
  onDownloadDoc?: (doc: any) => void;
  emptyActiveMessage?: string;
  emptyPastTitle?: string;
  emptyPastMessage?: string;
  emptyArchiveTitle?: string;
  emptyArchiveMessage?: string;
  restStyle?: any;
  tabStyle?: any;
}

const RentReviewTabs: React.FC<RentReviewTabsProps> = ({
  loading = false,
  rentReviewData,
  activeReview: activeReviewProp,
  userId: userIdProp,
  subTenantId: subTenantIdProp,
  statusLabel: statusLabelProp,
  documents: documentsProp,
  isResponded: isRespondedProp,
  pastReviews: pastReviewsProp,
  archivedReviews: archivedReviewsProp,
  items = TAB_LABELS,
  activeTab: activeTabProp,
  onTabChange,
  onRejectOffer,
  onSignAgreement,
  onContactAgent,
  onDownloadDoc,
  emptyActiveMessage = "You don't have any active rent review request. Kindly contact your agent for more info.",
  emptyPastTitle = 'No Past Reviews',
  emptyPastMessage = 'Completed rent reviews will appear here once your current review is finalised.',
  emptyArchiveTitle = 'No Archived Reviews',
  emptyArchiveMessage = 'Archived rent reviews will appear here.',
  restStyle,
  tabStyle,
}) => {
  const themeObj = getTokens('light').colors;
  const [internalActiveTab, setInternalActiveTab] = useState(0);

  const activeTab = activeTabProp !== undefined ? activeTabProp : internalActiveTab;

  const handleTabChange = (index: number) => {
    if (onTabChange) {
      onTabChange(index);
    }
    setInternalActiveTab(index);
  };

  const activeReview =
    activeReviewProp !== undefined
      ? activeReviewProp
      : (rentReviewData?.data?.active || rentReviewData?.active);

  const effectiveUserId = userIdProp;
  const authProfile = useSelector((state: any) => state?.authReducer?.profile?.data);
  const currentAppUserId =
    effectiveUserId ||
    authProfile?.appUser?.app_user_id ||
    authProfile?.app_user?.app_user_id ||
    authProfile?.app_user_id;
  const currentUserId =
    authProfile?.user_id ||
    authProfile?.id ||
    authProfile?.user?.user_id ||
    authProfile?.user?.id ||
    authProfile?.appUser?.user_id ||
    authProfile?.appUser?.user?.user_id;
  const currentEmail = (
    authProfile?.email ||
    authProfile?.user?.email ||
    authProfile?.appUser?.email ||
    authProfile?.appUser?.user?.email ||
    ''
  ).toLowerCase().trim();

  const tenantsList = activeReview?.open_flow_rent_review_tenants || [];
  const tenantObj =
    tenantsList.find((t: any) => {
      const sub = t?.OpenFlowSubTenant || t?.open_flow_sub_tenant || t?.openFlowSubTenant || t;
      const subAppUser = sub?.app_user || sub?.appUser;
      const subUser = subAppUser?.user || sub?.user;

      const subAppUserId = sub?.app_user_id || subAppUser?.app_user_id || subAppUser?.app_us || t?.app_user_id;
      const subUserId = subUser?.user_id || subUser?.id || subAppUser?.user_id || subAppUser?.user_i || sub?.user_id || t?.user_id;
      const subEmail = (subUser?.email || subUser?.e || sub?.email || subAppUser?.email || t?.email || '').toLowerCase().trim();

      if (subTenantIdProp && (t?.open_flow_sub_tenant_id == subTenantIdProp || sub?.open_flow_sub_t == subTenantIdProp)) {
        return true;
      }
      if (currentAppUserId && subAppUserId && String(subAppUserId) === String(currentAppUserId)) {
        return true;
      }
      if (currentUserId && subUserId && String(subUserId) === String(currentUserId)) {
        return true;
      }
      if (currentEmail && subEmail && currentEmail === subEmail) {
        return true;
      }
      return false;
    }) ||
    (tenantsList.length === 1 ? tenantsList[0] : null);

  const hasEsignatureRequest = Boolean(tenantObj ? tenantObj.envtoken : tenantsList.some((t: any) => t?.envtoken));
  const isSigned = hasEsignatureRequest
    ? Boolean(tenantObj?.is_signed || tenantObj?.signed_at || tenantObj?.tenant_status === 'signed')
    : Boolean(
        tenantObj?.is_signed ||
        tenantObj?.signed_at ||
        tenantObj?.tenant_status === 'signed' ||
        activeReview?.is_rent_review_signed
      );

  // Derive response state
  const isAccepted = hasEsignatureRequest
    ? isSigned
    : Boolean(
        tenantObj?.accepted_at ||
        tenantObj?.tenant_status === 'accepted' ||
        tenantObj?.tenant_status === 'signed'
      );
  const isRejected = Boolean(
    tenantObj?.rejected_at ||
    tenantObj?.tenant_status === 'rejected' ||
    activeReview?.rent_review_status === 4 ||
    activeReview?.rent_review_status === 5 ||
    activeReview?.rent_review_status_label === 'REJECTED' ||
    activeReview?.rejected_at
  );

  const isAllSigned = Boolean(activeReview?.is_rent_review_signed || activeReview?.signed_agreement_url);

  let showActionButtons = false;
  let showRejectButton = false;
  let finalSignButtonText = 'Accept (E-sign)';

  if (isAllSigned) {
    showActionButtons = false;
    showRejectButton = false;
  } else if (hasEsignatureRequest) {
    showActionButtons = !isSigned && !isRejected;
    showRejectButton = !isSigned && !isRejected;
  } else if (!isAccepted && !isRejected) {
    showActionButtons = activeReview?.rent_review_status === 2; // AWAITING_RESPONSE
    showRejectButton = true;
  }

  const derivedStatusLabel = isRejected
    ? 'Rejected'
    : (isAllSigned || isSigned || isAccepted)
      ? 'Accepted (Signed)' 
      : (hasEsignatureRequest && !isSigned)
        ? 'e-sign requested' 
        : activeReview?.rent_review_status_label || 'Awaiting Agent';
  const statusLabel = statusLabelProp !== undefined ? statusLabelProp : derivedStatusLabel;

  const derivedDocuments: DocumentItem[] = [];
  if (activeReview) {
    // 1. Signed Agreement (Highlighted when all parties signed)
    if (isAllSigned) {
      derivedDocuments.push({
        id: 'signed_agreement',
        title: 'Signed Rent Review Agreement',
        date: activeReview.completed_at 
          ? formatDate(activeReview.completed_at) 
          : (activeReview.agreement_sent_at ? formatDate(activeReview.agreement_sent_at) : '—'),
        status: 'Download',
        isSigned: true,
        rentReviewId: activeReview.open_flow_rent_review_id || activeReview.id,
      });
    }

    // 2. Section 4A Prescribed Notice
    if (activeReview.section4a_doc_url) {
      derivedDocuments.push({
        id: 'section4a',
        title: 'Section 4A Prescribed Doc',
        date: activeReview.createdAt ? formatDate(activeReview.createdAt) : '—',
        status: 'View',
        url: activeReview.section4a_doc_url,
      });
    }

    // 3. Draft Agreement (Only display draft if not yet signed)
    if (activeReview.agreement_doc_url && !isAllSigned) {
      derivedDocuments.push({
        id: 'agreement',
        title: 'Rent Review Agreement (Draft)',
        date: activeReview.agreement_sent_at ? formatDate(activeReview.agreement_sent_at) : '—',
        status: 'View',
        url: activeReview.agreement_doc_url,
      });
    }
  }
  const documents = documentsProp !== undefined ? documentsProp : derivedDocuments;

  const infoMessage = (isSigned && !isAllSigned && !isRejected)
    ? 'Your signature is recorded. The signed agreement will be ready for download once all parties have signed.'
    : undefined;

  // Derive past and archive reviews
  const rawPast = rentReviewData?.data?.past || rentReviewData?.past || [];
  const rawArchive = rentReviewData?.data?.archive || rentReviewData?.archive || [];

  const formatPreviousRent = (item: any) => {
    const val = item.current_rent || item.previous_rent;
    if (val) return `£${val}`;
    return item.previousRent || '—';
  };

  const derivedPastReviews: PastReviewItem[] = rawPast.map((item: any) => {
    let status = item.rent_review_status_label || item.status || 'Completed';
    const sLower = String(status).toLowerCase();
    if (sLower.includes('accept') || sLower.includes('sign') || item.is_rent_review_signed) {
      status = 'Accepted (Signed)';
    } else if (sLower.includes('reject') || sLower.includes('frozen')) {
      status = 'Rejected';
    }
    return {
      ...item,
      date: formatDate(item.createdAt || item.date),
      status,
      previousRent: formatPreviousRent(item),
      proposedRent: item.proposed_rent ? `£${item.proposed_rent}` : item.proposedRent || '—',
      section4a_doc_url: item.section4a_doc_url,
      agreement_doc_url: item.agreement_doc_url,
      is_rent_review_signed: Boolean(item.is_rent_review_signed || item.signed_agreement_url),
      signed_agreement_url: item.signed_agreement_url,
      open_flow_rent_review_id: item.open_flow_rent_review_id || item.id,
    };
  });

  const derivedArchivedReviews: PastReviewItem[] = rawArchive.map((item: any) => ({
    ...item,
    date: formatDate(item.createdAt || item.date),
    status: item.rent_review_status_label || item.status || 'Archived',
    previousRent: formatPreviousRent(item),
    proposedRent: item.proposed_rent ? `£${item.proposed_rent}` : item.proposedRent || '—',
    section4a_doc_url: item.section4a_doc_url,
    agreement_doc_url: item.agreement_doc_url,
    is_rent_review_signed: Boolean(item.is_rent_review_signed || item.signed_agreement_url),
    signed_agreement_url: item.signed_agreement_url,
    open_flow_rent_review_id: item.open_flow_rent_review_id || item.id,
  }));

  const pastReviews = pastReviewsProp !== undefined ? pastReviewsProp : derivedPastReviews;
  const archivedReviews =
    archivedReviewsProp !== undefined ? archivedReviewsProp : derivedArchivedReviews;

  return (
    <View style={[styles.container, restStyle]}>
      {/* ── Tab Bar (ODS Design System) ── */}
      <ODSTabs
        items={items}
        activeTab={activeTab}
        selectedColor={themeObj?.main?.blue?.[700] || '#1D2939'}
        color={themeObj?.main?.blue?.[500] || '#667085'}
        onTabChange={handleTabChange}
        restStyle={[styles.tabBar, tabStyle]}
      />

      {/* ── Tab Content ── */}
      {activeTab === 0 ? (
        // ── TAB 1: Active Rent Review ──
        loading ? (
          <RentReviewSkeleton />
        ) : !activeReview ? (
          <RentReviewDetailCard
            isEmpty
            emptyMessage={emptyActiveMessage}
            onContactAgent={onContactAgent}
          />
        ) : (
          <RentReviewDetailCard
            newRentAmount={`£${activeReview.proposed_rent || '0.00'}`}
            statusTagText={statusLabel}
            infoMessage={infoMessage}
            documents={documents.length > 0 ? documents : undefined}
            showButtons={showActionButtons}
            showRejectButton={showRejectButton}
            rejectButtonText="Reject"
            signButtonText={finalSignButtonText}
            onRejectOffer={onRejectOffer}
            onSignAgreement={() => {
              const signToken =
                tenantObj?.envtoken ||
                tenantsList.find((t: any) => t?.envtoken && !t?.is_signed)?.envtoken ||
                tenantsList.find((t: any) => t?.envtoken)?.envtoken;

              if (signToken) {
                const yotiSignatureBaseURL = 'https://www.yotisign.com/sign/';
                const esignatureURL = `${yotiSignatureBaseURL}${signToken}`;
                if (Platform.OS === 'web') {
                  window.open(esignatureURL, '_blank', 'noreferrer');
                } else {
                  Linking.openURL(esignatureURL);
                }
              } else if (onSignAgreement) {
                onSignAgreement();
              }
            }}
            onDownloadDoc={onDownloadDoc}
          />
        )
      ) : activeTab === 1 ? (
        // ── TAB 2: Past Rent Reviews ──
        <View style={styles.pastContainer}>
          {loading ? (
            <RentReviewSkeleton />
          ) : pastReviews.length === 0 ? (
            <View style={styles.emptyPast}>
              <View style={styles.emptyIconCircle}>
                <ODSIcon icon="TB-Alert" size={28} color="#9575CD" />
              </View>
              <ODSText type="h4" color="#101828" textAlign="center" style={{ fontWeight: '700', marginTop: 12 }}>
                {emptyPastTitle}
              </ODSText>
              <ODSText type="md" color="#667085" textAlign="center" style={{ marginTop: 8, lineHeight: 22 }}>
                {emptyPastMessage}
              </ODSText>
            </View>
          ) : (
            pastReviews.map((item, idx) => (
              <PastReviewCard
                key={item.open_flow_rent_review_id || idx}
                item={item}
                onDownloadDoc={onDownloadDoc}
              />
            ))
          )}
        </View>
      ) : (
        // ── TAB 3: Archived Rent Reviews ──
        <View style={styles.pastContainer}>
          {loading ? (
            <RentReviewSkeleton />
          ) : archivedReviews.length === 0 ? (
            <View style={styles.emptyPast}>
              <View style={styles.emptyIconCircle}>
                <ODSIcon icon="TB-Alert" size={28} color="#9575CD" />
              </View>
              <ODSText type="h4" color="#101828" textAlign="center" style={{ fontWeight: '700', marginTop: 12 }}>
                {emptyArchiveTitle}
              </ODSText>
              <ODSText type="md" color="#667085" textAlign="center" style={{ marginTop: 8, lineHeight: 22 }}>
                {emptyArchiveMessage}
              </ODSText>
            </View>
          ) : (
            archivedReviews.map((item, idx) => (
              <PastReviewCard
                key={item.open_flow_rent_review_id || idx}
                item={item}
                onDownloadDoc={onDownloadDoc}
              />
            ))
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  tabBar: {
    marginBottom: 20,
  },
  pastContainer: {
    width: '100%',
  },
  emptyPast: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    paddingVertical: 48,
    paddingHorizontal: 24,
    alignItems: 'center',
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
    borderWidth: 1,
    borderColor: '#E4E7EC',
  },
  emptyIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#EDE7F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const pastStyles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#E4E7EC',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  badge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  row: {
    marginBottom: 4,
  },
  docsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  docBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#1FA6B2',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: '#F0FAFB',
  },
});

export default RentReviewTabs;
